import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { BellIcon } from "@phosphor-icons/react";
import { api } from "@/convex/_generated/api";
import { useLocalization } from "@/context/LocalizationContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const NotificationsSettingsPage = () => {
  const { locale, setLocale, t } = useLocalization();
  const settingsQuery = useQuery(api.settings.getSettings);
  const updateSettings = useMutation(api.settings.updateSettings);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localeSaving, setLocaleSaving] = useState(false);

  useEffect(() => {
    if (settingsQuery) {
      setSettings(settingsQuery);
    }
  }, [settingsQuery]);

  const handleSave = async () => {
    if (!settings) {
      return;
    }

    setSaving(true);
    try {
      const updated = await updateSettings({
        emailNotificationsEnabled: settings.emailNotificationsEnabled,
        reminderDaysBefore: settings.reminderDaysBefore,
        locale: settings.locale,
      });
      setSettings(updated);
      toast.success(t("settings.notifications.saveSuccess"));
    } catch (error) {
      toast.error(error.message || t("settings.notifications.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleLocaleChange = async (nextLocale) => {
    if (!settings || nextLocale === settings.locale) {
      return;
    }

    const previousLocale = settings.locale ?? locale;
    const optimisticSettings = {
      ...settings,
      locale: nextLocale,
    };

    setLocale(nextLocale);
    setSettings(optimisticSettings);
    setLocaleSaving(true);

    try {
      const updated = await updateSettings({
        emailNotificationsEnabled: optimisticSettings.emailNotificationsEnabled,
        reminderDaysBefore: optimisticSettings.reminderDaysBefore,
        locale: nextLocale,
      });
      setSettings(updated);
    } catch (error) {
      setLocale(previousLocale);
      setSettings({
        ...optimisticSettings,
        locale: previousLocale,
      });
      toast.error(error.message || t("settings.language.saveFailed"));
    } finally {
      setLocaleSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-6">
        <div className="text-zinc-500 dark:text-zinc-400">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none p-4 sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-white dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 rounded-none">
            <BellIcon
              size={22}
              className="text-[#002FA7] dark:text-blue-400"
              weight="regular"
            />
          </div>
          <div>
            <h3 className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
              {t("settings.notifications.title")}
            </h3>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col gap-4 border-b border-zinc-200 dark:border-zinc-700 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <p className="font-dmsans text-base font-medium text-zinc-900 dark:text-zinc-100">
                {t("settings.notifications.emailTitle")}
              </p>
              <p className="mt-1 font-dmsans text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                {t("settings.notifications.emailDescription")}
              </p>
            </div>
            <label className="relative inline-flex cursor-pointer items-center self-start sm:self-center">
              <input
                data-testid="email-notifications-toggle"
                type="checkbox"
                checked={settings.emailNotificationsEnabled}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    emailNotificationsEnabled: event.target.checked,
                  })
                }
                className="peer sr-only"
              />
              <div className="h-6 w-11 rounded-none bg-zinc-200 transition-colors after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-none after:border after:border-zinc-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#002FA7] peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-zinc-700" />
            </label>
          </div>

          <div className="border-b border-zinc-200 dark:border-zinc-700 pb-6">
            <p className="font-dmsans text-base font-medium text-zinc-900 dark:text-zinc-100">
              {t("settings.notifications.timingTitle")}
            </p>
            <p className="mb-4 font-dmsans text-sm leading-6 text-zinc-500 dark:text-zinc-400">
              {t("settings.notifications.timingDescription")}
            </p>
            <Select
              value={settings.reminderDaysBefore.toString()}
              onValueChange={(value) =>
                setSettings({
                  ...settings,
                  reminderDaysBefore: parseInt(value, 10),
                })
              }
              disabled={!settings.emailNotificationsEnabled}
            >
              <SelectTrigger
                data-testid="reminder-days-select"
                className="w-full sm:max-w-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
                <SelectItem value="1">
                  {t("settings.notifications.options.one")}
                </SelectItem>
                <SelectItem value="2">
                  {t("settings.notifications.options.two")}
                </SelectItem>
                <SelectItem value="3">
                  {t("settings.notifications.options.three")}
                </SelectItem>
                <SelectItem value="5">
                  {t("settings.notifications.options.five")}
                </SelectItem>
                <SelectItem value="7">
                  {t("settings.notifications.options.seven")}
                </SelectItem>
                <SelectItem value="14">
                  {t("settings.notifications.options.fourteen")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="font-dmsans text-base font-medium text-zinc-900 dark:text-zinc-100">
              {t("settings.language.title")}
            </p>
            <p className="mb-4 font-dmsans text-sm leading-6 text-zinc-500 dark:text-zinc-400">
              {t("settings.language.description")}
            </p>
            <Select
              value={settings.locale}
              onValueChange={handleLocaleChange}
              disabled={localeSaving}
            >
              <SelectTrigger className="w-full sm:max-w-xs bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
                <SelectItem value="en">
                  {t("common.languages.en")}
                </SelectItem>
                <SelectItem value="sk">
                  {t("common.languages.sk")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 border-t border-zinc-200 dark:border-zinc-700 pt-6">
          <button
            data-testid="save-settings-button"
            onClick={handleSave}
            disabled={saving}
            className="flex min-h-11 items-center justify-center bg-[#002FA7] text-white rounded-none px-6 py-2.5 hover:bg-[#001B61] font-medium text-sm disabled:opacity-50"
          >
            {saving ? t("settings.notifications.saving") : t("settings.notifications.save")}
          </button>
        </div>
      </section>
    </div>
  );
};

export default NotificationsSettingsPage;
